import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { User } from '../login/loginAPI';
import profileAPI, { ProfileUpdate } from './profileAPI';
import { getErrorMessage } from '../../utils/errorHandler';
import { RootState } from '../../store';

export interface Profile extends User {
  photo: string;
};

interface ProfileState {
  status: 'loading' | 'idle' | 'error' | 'success';
  error?: string;
  current?: Profile;
}

export const initialState: ProfileState = {
  status: 'idle',
};

export const getProfile = createAsyncThunk<
  Profile,
  void,
  { rejectValue: string}
>('profile/get', async (_, thunkAPI) => {
  try {
    const response = await profileAPI.getProfile();

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const updateProfile = createAsyncThunk<
  Profile,
  ProfileUpdate,
  { rejectValue: string }
>('profile/update', async (params: ProfileUpdate, thunkAPI) => {
  try {
    const response = await profileAPI.updateProfile(params);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    cleanState(state) {
      state.status = 'idle';
      state.error = undefined;
      state.current = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(updateProfile.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.status = 'success';
        state.current = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message ? action.error.message : '';
      });

    builder
      .addCase(getProfile.pending, state => {
        state.status = 'loading';
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.status = 'success';
        state.current = action.payload;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message ? action.error.message : '';
      });
  },
});

export const getProfileStatus = (state: RootState) => state.profile.status;
export const getProfileCurrent = (state: RootState) => state.profile.current;

export const { cleanState } = profileSlice.actions;

export default profileSlice.reducer;
